import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type BoardDetail,
  type Card,
  type Column,
  columnAppearanceStyles,
  columnProps,
  type Label,
  type SortCriterion,
} from '@/entities/board';
import { MoveColumnToBoardDialog } from '@/features/board-move';
import { isNote } from '@/entities/note';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { type AvatarPerson, Button, ConfirmDialog, HelpTip } from '@/shared/ui';

import { CardTile } from './CardTile';
import { ColumnAppearanceDialog } from './ColumnAppearanceDialog';
import { ColumnDescriptionDialog } from './ColumnDescriptionDialog';
import { ColumnMenu } from './ColumnMenu';
import { ColumnTitleEditor } from './ColumnTitleEditor';
import { columnCardsData, columnData, isCard, isColumn } from './dnd';
import { InlineForm } from './InlineForm';
import styles from './Kanban.module.css';

// The Markdown renderer stays out of the main bundle (shared with the card popup chunk).
const ColumnDescription = lazy(() =>
  import('./ColumnDescription').then((module) => ({ default: module.ColumnDescription })),
);

const COLUMN_NAME_MAX = 100;
const CARD_TITLE_MAX = 500;

export interface ColumnActions {
  rename: (columnId: string, name: string) => Promise<unknown>;
  /** Markdown; an empty string clears the description. */
  setDescription: (columnId: string, description: string) => Promise<unknown>;
  move: (columnId: string, direction: 'left' | 'right') => void;
  remove: (columnId: string) => Promise<unknown>;
  copy: (columnId: string) => Promise<unknown>;
  /** One-time permanent sort of the column's cards (server side). */
  sort: (columnId: string, criterion: SortCriterion) => void;
  addCard: (columnId: string, title: string) => Promise<unknown>;
  openCard: (cardId: string) => void;
  toggleComplete: (cardId: string, completed: boolean, anchor?: Element) => void;
  archive: (cardId: string) => void;
  unpin: (cardId: string) => void;
  /** A pin/unpin request for this card is in flight. */
  isPinPending: (cardId: string) => boolean;
}

interface ColumnViewProps {
  column: Column;
  /** The board this column is on (the move dialog needs its workspace and cards). */
  board: BoardDetail;
  cards: Card[];
  labels: Label[];
  /** Total cards before filtering (shown when a filter hides some). */
  totalCards: number;
  filtering: boolean;
  isFirst: boolean;
  isLast: boolean;
  actions: ColumnActions;
  /** card id -> executors (names resolved by the board). */
  assigneesByCard?: ReadonlyMap<string, readonly AvatarPerson[]>;
}

export function ColumnView({
  column,
  board,
  cards,
  labels,
  totalCards,
  filtering,
  isFirst,
  isLast,
  actions,
  assigneesByCard,
}: ColumnViewProps) {
  const { t } = useTranslation();
  const columnRef = useRef<HTMLElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [renaming, setRenaming] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingAppearance, setEditingAppearance] = useState(false);
  const [movingToBoard, setMovingToBoard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cardOver, setCardOver] = useState(false);
  const [columnEdge, setColumnEdge] = useState<Edge | null>(null);
  // The system Calendar column has a localized, fixed name and no copy / move / delete.
  const system = column.system_kind === 'calendar';
  const displayName = system ? t('columns.calendar') : column.name;
  // Reflection of the permissions the server sent; the server enforces every action.
  const canColumns = can(board.permissions, PERMISSIONS.boardManageColumns);
  const canCreate = can(board.permissions, PERMISSIONS.taskCreate);
  const canMove = can(board.permissions, PERMISSIONS.taskMove);

  useEffect(() => {
    const element = columnRef.current;
    const handle = handleRef.current;
    const list = listRef.current;
    if (!element || !handle || !list) return;
    return combine(
      draggable({
        element,
        dragHandle: handle,
        canDrag: () => !filtering && canColumns,
        getInitialData: () => columnData(column.id),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => isColumn(source.data) && source.data.columnId !== column.id,
        getData: ({ input }) =>
          attachClosestEdge(columnData(column.id), {
            element,
            input,
            allowedEdges: ['left', 'right'],
          }),
        onDrag: ({ self }) => {
          setColumnEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setColumnEdge(null);
        },
        onDrop: () => {
          setColumnEdge(null);
        },
      }),
      dropTargetForElements({
        element: list,
        canDrop: ({ source }) =>
          ((isCard(source.data) && canMove) || (isNote(source.data) && canCreate)) && !filtering,
        getData: () => columnCardsData(column.id),
        onDragEnter: () => {
          setCardOver(true);
        },
        onDragLeave: () => {
          setCardOver(false);
        },
        onDrop: () => {
          setCardOver(false);
        },
      }),
    );
  }, [column.id, filtering, canColumns, canCreate, canMove]);

  return (
    <section
      ref={columnRef}
      className={`${styles.column} ${columnAppearanceStyles.frame}`}
      aria-label={displayName}
      data-testid="column"
      data-system={system ? 'calendar' : undefined}
      {...columnProps(column)}
    >
      {columnEdge && (
        <span className={`${styles.columnDropLine} ${styles[`drop-${columnEdge}`]}`} aria-hidden />
      )}
      <header
        className={`${styles.columnHeader} ${columnAppearanceStyles.head} ${columnAppearanceStyles.chrome}`}
      >
        {renaming && !system && canColumns ? (
          <ColumnTitleEditor
            name={column.name}
            maxLength={COLUMN_NAME_MAX}
            onSave={(name) => actions.rename(column.id, name)}
            onClose={() => {
              setRenaming(false);
            }}
          />
        ) : (
          <>
            <div ref={handleRef} className={styles.columnHandle} title={t('columns.dragHint')}>
              <h2 className={styles.columnTitle}>
                {/* A span, not a <button>: Firefox cannot start a drag on a button. */}
                {system || !canColumns ? (
                  <span className={styles.columnTitleStatic}>{displayName}</span>
                ) : (
                  <span
                    role="button"
                    tabIndex={0}
                    className={styles.columnTitleButton}
                    title={t('columns.renameHint')}
                    onClick={() => {
                      setRenaming(true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setRenaming(true);
                      }
                    }}
                  >
                    {column.name}
                  </span>
                )}
              </h2>
              <span
                className={`${styles.count} ${columnAppearanceStyles.badge}`}
                aria-label={
                  filtering
                    ? t('columns.filteredCount', { count: cards.length, total: totalCards })
                    : t('columns.cardCount', { count: cards.length })
                }
              >
                {filtering ? `${cards.length}/${totalCards}` : cards.length}
              </span>
              {system && (
                <HelpTip topic={displayName} topicId="kanban" text={t('help.calendarColumn')} />
              )}
            </div>
            {canColumns && (
              <ColumnMenu
                columnName={displayName}
                system={system}
                sortDisabledReason={filtering ? t('columns.sort.filterActive') : null}
                onSort={(criterion) => {
                  actions.sort(column.id, criterion);
                }}
                canMoveLeft={!isFirst}
                canMoveRight={!isLast}
                hasDescription={column.description !== null}
                onRename={() => {
                  setRenaming(true);
                }}
                onMove={(direction) => {
                  actions.move(column.id, direction);
                }}
                onEditDescription={() => {
                  setEditingDescription(true);
                }}
                onEditAppearance={() => {
                  setEditingAppearance(true);
                }}
                onCopy={() => {
                  void actions.copy(column.id);
                }}
                onMoveToBoard={() => {
                  setMovingToBoard(true);
                }}
                onDelete={() => {
                  setConfirmDelete(true);
                }}
              />
            )}
          </>
        )}
      </header>
      <div className={styles.columnBody}>
        {column.description && (
          <Suspense fallback={null}>
            <ColumnDescription
              text={column.description}
              onEdit={() => {
                if (canColumns) setEditingDescription(true);
              }}
            />
          </Suspense>
        )}

        <div ref={listRef} className={`${styles.cardList} ${cardOver ? styles.cardListOver : ''}`}>
          {cards.map((card) => (
            <CardTile
              key={card.id}
              card={card}
              labels={labels}
              columnLook={column.card_look}
              draggableEnabled={!filtering && canMove}
              canEdit={
                can(board.permissions, PERMISSIONS.taskEditUnassigned) ||
                can(board.permissions, PERMISSIONS.taskEditShared) ||
                can(board.permissions, PERMISSIONS.taskEditAssignedToMe) ||
                can(board.permissions, PERMISSIONS.taskEditAssignedToOthers)
              }
              {...(assigneesByCard?.get(card.id) && { assignees: assigneesByCard.get(card.id) })}
              onOpen={actions.openCard}
              onToggleComplete={actions.toggleComplete}
              onArchive={actions.archive}
              onUnpin={actions.unpin}
              pinPending={actions.isPinPending(card.id)}
            />
          ))}
          {cards.length === 0 && !adding && (
            <p className={`${styles.emptyColumn} ${columnAppearanceStyles.chrome}`}>
              {filtering && totalCards > 0 ? t('columns.noMatches') : t('columns.empty')}
            </p>
          )}
        </div>
      </div>

      {canCreate && (
        <footer className={`${styles.columnFooter} ${columnAppearanceStyles.chrome}`}>
          {adding ? (
            <InlineForm
              label={t('cards.title')}
              submitLabel={t('cards.add')}
              maxLength={CARD_TITLE_MAX}
              // Closes once the server confirmed the card; a failure keeps the form and the text.
              onSubmit={(title) =>
                actions.addCard(column.id, title).then(() => {
                  setAdding(false);
                })
              }
              onCancel={() => {
                setAdding(false);
              }}
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className={styles.addCard}
              onClick={() => {
                setAdding(true);
              }}
            >
              + {t('cards.add')}
            </Button>
          )}
        </footer>
      )}

      {editingDescription && (
        <ColumnDescriptionDialog
          columnName={column.name}
          initialValue={column.description ?? ''}
          onSave={(description) => actions.setDescription(column.id, description)}
          onClose={() => {
            setEditingDescription(false);
          }}
        />
      )}

      {editingAppearance && (
        <ColumnAppearanceDialog
          column={column}
          boardId={board.board.id}
          onClose={() => {
            setEditingAppearance(false);
          }}
        />
      )}

      {movingToBoard && (
        <MoveColumnToBoardDialog
          column={column}
          source={board}
          onClose={() => {
            setMovingToBoard(false);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={t('columns.deleteTitle')}
          message={t('columns.deleteMessage', { name: column.name, count: cards.length })}
          confirmLabel={t('common.delete')}
          onCancel={() => {
            setConfirmDelete(false);
          }}
          onConfirm={() => {
            setConfirmDelete(false);
            void actions.remove(column.id);
          }}
        />
      )}
    </section>
  );
}
